<template>
    <v-container fluid>
        <div v-if="!debitNoteLoading">
            <v-row>
                <v-col>
                    <h1 class="text-h4">Debit Note #{{data.code}}</h1>
                    <p class="text-subtitle-1">Modify supplier debit note</p>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-btn block @click="exchangeRateIndexShown = true" depressed>
                        <v-icon left>mdi-chart-areaspline-variant</v-icon>
                        currency rates
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <DebitNoteForm 
                        ref="form"
                        v-model="data"
                        v-if="!debitNoteLoading"
                        edit-mode
                        :exchange-rate-index-shown.sync="exchangeRateIndexShown"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3">
                    <v-btn block class="primary" @click="handleSave" :loading="loading">
                        <v-icon left>mdi-content-save</v-icon>
                        save
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3">
                    <v-btn block depressed @click="handleDiscard">
                        cancel
                    </v-btn>
                </v-col>
            </v-row>
        </div>
        <div v-else>
            <v-row>
                <v-col>
                    <v-skeleton-loader type="heading" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="d-flex">
                    <v-skeleton-loader type="button" class="my-4 me-4"/>
                    <v-skeleton-loader type="button" class="my-4"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
        </div>
        <PAlert v-model="alert.value" v-bind="alert"></PAlert>
    </v-container>
</template>

<script>
import permissions from './../../data/permissions';
import DebitNoteForm from "./../../components/purchase/DebitNoteForm.vue"
import PAlert from "./../../components/common/PAlert.vue"
import { mapActions, mapGetters } from 'vuex';
export default {
    name: "DebitNoteEditView",
    components: {
        DebitNoteForm,
        PAlert
    },
    data() {
        return {
            id: null,
            loading: false,
            debitNoteLoading: false,
            alert: {
                value: false,
                title: "Alert",
                content: "Are You Sure",
                callback: null
            },
            data: null,
            perm: permissions,
            exchangeRateIndexShown: false
        }
    },
    computed: {
        ...mapGetters({
            hasPermission: "authentication/hasPermission"
        })
    },
    methods: {
        ...mapActions({
            findByID : "purchase/debitNote/findByID",
            pushSupplier: "purchase/supplier/push",
            pushLedgerAccount: "purchase/ledgerAccount/push",
            update: "purchase/debitNote/update",
            remove: "purchase/debitNote/remove",
            snackbar: "ui/snackbar/show"

        }),
        handleSave() {
            this.alert = {...this.alert,content: "Are You Sure You Want to save?", value: true, callback: this.save };
        },
        save() {
            if(!this.$refs.form.validate()) return;
            this.loading = true;
            this.update(this.data).then(response => {
                this.$router.push({ name: "DebitNoteDisplay", params: { id: response.data.id } })
                this.snackbar({text: "Debit Note Updated Succesfuly", color: "success"})
            }).catch(error => {
                let message = error.response ? error.response.data.message : error;
                this.snackbar({text: message, color: "error"})
            }).finally(() => {
                this.loading = false
            })
        },
        handleNewLine() {
            this.$refs.form.addLine()
        },
        handleDiscard() {
            this.alert = {...this.alert,content: "Are You Sure You Want To Discard", value: true, callback: this.discard }
        },
        handleDelete() {
            this.alert = {...this.alert,content: "Are You Sure You Want to delete?", value: true, callback: this.deleteAsync }
        },
        deleteAsync() {
            this.loading = true;
            this.remove(this.data).then(() => {
                this.snackbar({text: "Debit Note deleted", color: "success"})
                this.$router.push({ name: "DebitNoteList"})
            }).catch(error => {
                let message = error.response ? error.response.data.message : error
                this.snackbar({ text: message, color: "error"})
            }).finally(() => {
                this.loading = false
            })
        },
        discard() {
            this.$router.push({ name: "DebitNoteDisplay", params: { id: this.id}})
        },
        formatDebitNoteForm(debitNote) {
            this.pushSupplier(debitNote.supplier)
            let exchangeRate = debitNote.base_amount / debitNote.amount;
            return {
                ...debitNote,
                supplier_id : debitNote.supplier.id,
                account_id : debitNote.account.id,
                lines : debitNote.lines.map(line => {
                    this.pushLedgerAccount(line.ledger_account)
                    return{
                        ...line,
                        ledger_account_id : line.ledger_account.id,
                        document_amount: line.base_amount / exchangeRate,
                    }
                })
            }
        },
        fetchDebitNote() {
            this.debitNoteLoading = true 
            this.findByID(this.id).then(response => {
                this.data = this.formatDebitNoteForm(response.data)
            }).catch(error => {
                let message = error.response ? error.response.data.message : error
                this.snackbar({color: "error", text: message })
            }).finally(() => {
                this.debitNoteLoading = false
            })
        },
    },
    mounted() {
        this.id = this.$route.params.id
        this.fetchDebitNote();
    },
    beforeRouterUpdate(to, from, next){
        this.id = to.params.id;
        this.fetchDebitNote();
        next();
    }
}
</script>

<style>

</style>